import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { setSnackbar } from "redux/actions/search/searchActions";
import { PROYECT, api_post } from "services/api";
import { removeKeys } from "helpers/removeKeys";
import ErrorFallback from "./ErrorFallback";
import { ENERSI_VERSION } from "utils/variables";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.cleanupObserver = null;
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      componentKey: Date.now(),
      currentPath: props.location.pathname,
      errorCount: 0,
      lastErrorType: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidMount() {
    this.setState({ currentPath: this.props.location.pathname });
  }

  componentDidUpdate(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.handlePathChange();
    }
  }

  handlePathChange = () => {
    this.setState({
      currentPath: this.props.location.pathname,
      errorCount: 0,
      lastErrorType: null,
    });
    if (this.state.hasError) {
      this.resetErrorState();
    }
  };

  componentDidCatch(error, errorInfo) {
    console.error("Error capturado por ErrorBoundary:", error, errorInfo);
    this.setState({ error, errorInfo });

    this.cleanupIframes();
    this.logErrorToService(error, errorInfo);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentWillUnmount() {
    if (this.cleanupObserver) {
      this.cleanupObserver.disconnect();
      this.cleanupObserver = null;
    }
  }

  cleanupIframes = () => {
    const removeIframes = () => {
      document.querySelectorAll("iframe").forEach((iframe) => {
        try {
          if (!document.contains(iframe)) return;
          if (iframe.closest && iframe.closest('#ccp-container')) return;

          const style = iframe.style.cssText;
          if (
            style.includes("position: fixed") &&
            style.includes("width: 100%") &&
            style.includes("height: 100%") &&
            style.includes("z-index: 2147483647")
          ) {
            iframe.remove();
            console.warn("Removed full-screen iframe after error.");
          }
        } catch (e) {
          // Iframe ya fue removido por reconciliación de React
        }
      });
    };

    removeIframes();

    if (this.cleanupObserver) {
      this.cleanupObserver.disconnect();
    }
    this.cleanupObserver = new MutationObserver(removeIframes);
    this.cleanupObserver.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => {
      if (this.cleanupObserver) {
        this.cleanupObserver.disconnect();
        this.cleanupObserver = null;
      }
    }, 2000);
  };

  extractErrorLocation = (error) => {
    if (!error || !error.stack) return "Ubicación desconocida";
    const stackLines = error.stack.split('\n');
    for (const line of stackLines) {
      if (line.trim().startsWith('at ') || line.includes('@')) {
        return line.trim();
      }
    }
    return "No se pudo determinar la ubicación exacta";
  };

  logErrorToService = async (error, errorInfo) => {
    const { errorCount, lastErrorType } = this.state;
    const { dispatch, match } = this.props;

    const isSameError = lastErrorType === error?.message;
    const newErrorCount = isSameError ? errorCount + 1 : 1;

    if (newErrorCount > 2) {
      console.warn("Demasiados errores consecutivos, evitando envío de logs repetidos.");
      return;
    }

    this.setState({
      errorCount: newErrorCount,
      lastErrorType: error?.message,
    });

    const localInfo = removeKeys(window.localStorage, ["persist", "ally"]);
    const hasParams = Object.keys(match?.params || {}).length > 0;

    const errorMessage = `
    [GN4 - ERROR B.] 
    [VERSION - ${ENERSI_VERSION}]
    -------------------------------------
    Fecha: ${new Date().toLocaleString()}
    -------------------------------------
    MODULO: ${window.location.pathname}
    -------------------------------------
    Tipo: ${error.name}
    -------------------------------------
    Mensaje: ${error.message}
    -------------------------------------
    Stack: ${error.stack}
    -------------------------------------
    Ubicación (aprox): ${this.extractErrorLocation(error)}
    -------------------------------------
    Component Stack: ${errorInfo?.componentStack || "No component stack available"}
    -------------------------------------
    ${hasParams ? "Parámetros:" : ""}
    ${hasParams ? JSON.stringify(match?.params || {}, null, 2) : ""}
    ${hasParams ? "-------------------------------------" : ""}
    [Local-storage]:
    ${JSON.stringify(localInfo || {}, null, 2)}
    `.trim();

    try {
      if (newErrorCount <= 1) {
        const url = `${process.env.REACT_APP_URL}${PROYECT}/logger/front/slack/critical`;
        api_post(url, {
          url: window.location.href,
          message: errorMessage,
        }).catch(e => console.error("Failed to log error to server", e));
      }

      dispatch(setSnackbar(`Error: ${error.message}`));
    } catch (e) {
      console.error("Error reporting error", e);
    }
  };

  restoreReducer = () => {
    const { dispatch, restoreKey, match, history } = this.props;
    const hasValues = Object.keys(match.params).length > 0;

    if (restoreKey) {
      dispatch({ type: restoreKey });
    }

    if (hasValues && history.length > 1) {
      history.goBack();
    }

    this.resetErrorState();
  };

  resetErrorState = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      componentKey: Date.now(),
    });

    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          resetErrorBoundary={this.restoreReducer}
        />
      );
    }
    return <React.Fragment key={this.state.componentKey}>{this.props.children}</React.Fragment>;
  }
}

export default connect()(withRouter(ErrorBoundary));
